import { useState } from 'react';
import { RECURRENCE_LABELS, nextDueDate } from '../../lib/recurringBilling';
import WMIcon from './WMIcon';

/**
 * RecurringBillingModal
 * Props:
 *   invoice       — the invoice to make recurring
 *   onSave(recurrence) — called with recurrence config
 *   onClose
 */
export default function RecurringBillingModal({ invoice, onSave, onClose }) {
  const existing = invoice?.recurrence;
  const [frequency, setFrequency] = useState(existing?.frequency || 'monthly');
  const [endType, setEndType] = useState(existing?.maxCount ? 'count' : existing?.endDate ? 'date' : 'never');
  const [maxCount, setMaxCount] = useState(existing?.maxCount || 12);
  const [endDate, setEndDate] = useState(existing?.endDate?.split('T')[0] || '');

  const startDate = new Date().toISOString();
  const preview = nextDueDate({ active: true, frequency, startDate, nextDueDate: startDate, endDate: endType === 'date' ? endDate : null, maxCount: endType === 'count' ? maxCount : null, createdCount: 0 });

  const handleSave = () => {
    onSave({
      active: true,
      frequency,
      startDate,
      nextDueDate: startDate,
      endDate: endType === 'date' ? endDate : null,
      maxCount: endType === 'count' ? parseInt(maxCount) || null : null,
      createdCount: 0,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WMIcon name="arrow-path" className="w-4 h-4 text-blue-600" />
            <h2 className="text-[var(--wm-text-heading)] font-semibold text-gray-900 dark:text-gray-100">Make Recurring</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <WMIcon name="x-mark" className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Invoice summary */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
            <p className="text-[var(--wm-text-ui)] text-gray-500 dark:text-gray-400">Invoice</p>
            <p className="text-[var(--wm-text-body)] font-medium text-gray-900 dark:text-gray-100">{invoice?.invoiceNumber} · ${(+(invoice?.total ?? 0)).toFixed(2)}</p>
          </div>

          {/* Frequency */}
          <div>
            <label className="text-[var(--wm-text-ui)] font-medium text-gray-600 dark:text-gray-400 block mb-1">Frequency</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(RECURRENCE_LABELS).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setFrequency(value)}
                  className={`py-2 px-3 text-[var(--wm-text-ui)] rounded-lg border transition-colors text-left ${
                    frequency === value
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:border-blue-600 dark:text-blue-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* End condition */}
          <div>
            <label className="text-[var(--wm-text-ui)] font-medium text-gray-600 dark:text-gray-400 block mb-1">Ends</label>
            <div className="flex gap-2 mb-2">
              {[['never', 'Never'], ['count', 'After N times'], ['date', 'On date']].map(([v, l]) => (
                <button key={v} onClick={() => setEndType(v)} className={`flex-1 py-1.5 text-[var(--wm-text-ui)] rounded-lg border ${endType === v ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:border-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'}`}>{l}</button>
              ))}
            </div>
            {endType === 'count' && (
              <input type="number" min="1" value={maxCount} onChange={e => setMaxCount(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-[var(--wm-text-body)] dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Number of invoices" />
            )}
            {endType === 'date' && (
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-[var(--wm-text-body)] dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            )}
          </div>

          {/* Next invoice preview */}
          {preview && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2 text-[var(--wm-text-ui)] text-blue-700 dark:text-blue-400">
              Next invoice: {new Date(preview).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 text-[var(--wm-text-body)] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">Cancel</button>
          <button onClick={handleSave} className="flex-1 py-2 text-[var(--wm-text-body)] bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">Enable Recurring</button>
        </div>
      </div>
    </div>
  );
}
